// @flow
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useRouter } from 'Client/router/useRouter';
import { useRouterEvents } from 'Client/router/useRouterEvents';
import ReactResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { CriteriaContextProvider } from 'Components/widgets/criteria/CriteriaContext';
import { CriteriaDisplayProvider } from 'Components/widgets/criteria/CriteriaDisplayContext';
import {
  currentUrlContainsSearchQueryParam,
  currentUrlHasNoQueryParams,
  FACET_QUERY_PARAMS,
  navigateToSearchPage,
  navigateToResortPage,
  type SearchCriteria,
} from 'Components/widgets/facet-search/FacetSearch.utils';
import { ReactPageContext } from 'Context/pageContext';
import redirectUtils from 'Utils/uri/redirectUtils';
import { useTrackQueryChange } from 'Components/widgets/facet-search/useTrackQueryChange';
import { useTrackSearchResults } from 'Components/widgets/facet-search/useTrackSearchResults';
import {
  FacetSearchResults,
  type Props as FacetSearchResultsProps,
} from 'Components/widgets/facet-search-results/FacetSearchResults.jsx';
import {
  SearchForm,
  type Props as SearchFormProps,
} from 'Components/widgets/search-form/SearchForm';
import ReactAppContext from 'Context/ReactAppContext';
import * as pageIds from 'Routes/constants';
import {
  changeSearchQuery,
  resetRedirection,
  resetSearch,
  resetCriteria,
  setIsSearchEnabled,
} from 'Store/faceting/facetingSearch.action';
import {
  facetingLoadProductsRequest,
  facetingSearchRequest,
} from 'Store/faceting/searchRequest.action';
import {
  facetingCriteriaSelector,
  filteredProductsSelector,
  getKidsValidationState,
} from 'Store/faceting/faceting.selector';
import { setProductComparisonSelectionMode } from 'Store/faceting/productComparison.action';
import { startLoading } from 'Store/global-loader/globalLoader.action';
import { keywordsSelector } from 'Store/search/search.selector';
import {
  getDepartureDate,
  getTripDuration,
  getDepartureCity,
} from 'Store/trip-criteria/tripCriteria.selector';
import { getKids } from 'Store/trip-information/storeHelper';
import { RESORT_PRODUCT_TYPE } from 'Store/utils/autocompleteItemHelper';
import {
  DEPARTURE_DATE_MAX_FACET_ID,
  DEPARTURE_DATE_MIN_FACET_ID,
  PRODUCT_FACET_ID,
  DURATION_FACET,
} from 'Utils/faceting/constants';

export type Props = {
  boutique?: string,
  hideSearchInput?: $PropertyType<SearchFormProps, 'withQuery'>,
  i18n: CMSContent,
  initialIsSearchEnabled?: boolean,
  isProductComparisonModeAuthorized?: $PropertyType<
    FacetSearchResultsProps,
    'isProductComparisonModeAuthorized',
  >,
  offer?: Offer,
  initialProducts?: $ReadOnlyArray<OfferProduct>,
  resetSearchAtMount?: boolean,
  resultsHasSeeMore?: $PropertyType<FacetSearchResultsProps, 'resultsHasSeeMore'>,
  resultsSeeMore?: $PropertyType<FacetSearchResultsProps, 'resultsSeeMore'>,
  searchPageRouteName?: string,
  shouldPersistCriteriaInUrl?: boolean,
  shouldRedirectToResortPage?: boolean,
  shouldSetDefaultDepartureCityOnCompare?: $PropertyType<
    FacetSearchResultsProps,
    'shouldSetDefaultDepartureCityOnCompare',
  >,
  virtualizedGrid?: $PropertyType<FacetSearchResultsProps, 'virtualizedGrid'>,
  withSearchFacets?: $PropertyType<SearchFormProps, 'withFacets'>,
  withSearchForm?: boolean,
  withSearchResults?: boolean,
  isSynchronousGrid?: boolean,
};

export const FacetSearch = (props: Props) => {
  const {
    shouldRedirectToResortPage = false,
    withSearchResults = false,
    isSynchronousGrid = false,
  } = props;
  const appContext = useContext(ReactAppContext);
  const pageContext = useContext(ReactPageContext);
  const routerContext = useRouter<{}, SearchQueryParams>();
  const { location } = routerContext;
  const routerEvents = useRouterEvents();
  const responsiveContext = useContext(ReactResponsiveContext);
  const [searchDone, setSearchDone] = useState(false);

  const dispatch = useDispatch();
  const departureCity = useSelector(getDepartureCity);
  const departureDate = useSelector(getDepartureDate);
  const duration = useSelector(getTripDuration);
  const facetingCriteria = useSelector(facetingCriteriaSelector);
  const isLoading = useSelector(({ faceting }) => faceting.isLoading);
  const noResults = useSelector(({ faceting }) => faceting.noResults);
  const isSearchEnabled = useSelector(state => state.faceting.isSearchEnabled);
  const keywords = useSelector(keywordsSelector);
  const kids = useSelector(getKids);
  const kidsValidationState = useSelector(getKidsValidationState);
  const products: Array<Resort> = useSelector(state => filteredProductsSelector(state) || []);
  const productIds = products.map(({ resortId }) => resortId).join('|');
  const query = useSelector(state => state.faceting.query || {});
  const redirection = useSelector(state => state.faceting.redirection);

  const trackQueryChange = useTrackQueryChange();
  const trackSearchResults = useTrackSearchResults();

  const searchCriteria: SearchCriteria = {
    departureCity,
    departureDate,
    duration,
    keywords,
    kids,
    kidsValidationState,
    query,
  };

  const searchOptions: FacetingSearchOptions = useMemo(
    () => ({
      boutique: props.boutique,
      offerId: props.offer?.id,
      offerName: props.offer?.seoName,
      productIds: (props.initialProducts || []).map(product => product.id),
      query: query.label || '',
      screenSize: responsiveContext.currentBreakpoint,
    }),
    [
      props.boutique,
      props.initialProducts,
      props.offer,
      props.i18n,
      query,
      responsiveContext.currentBreakpoint,
    ],
  );

  const search = () => {
    // We check routerReady to ensure that we do not update URL
    // or results while the router is transitioning between routes.
    if (isSearchEnabled) {
      if (props.shouldPersistCriteriaInUrl && !routerEvents.transitioning) {
        navigateToSearchPage({
          criteriaKeysToPersist: FACET_QUERY_PARAMS,
          location,
          searchCriteria,
          searchPageRouteName: props.searchPageRouteName,
        });
      }

      dispatch(
        facetingSearchRequest({
          appContext,
          options: searchOptions,
        }),
      );

      setSearchDone(true);
    }
  };

  useEffect(
    () => {
      if (
        typeof products !== 'undefined' &&
        pageContext.pageId === pageIds.SEARCH_PAGE_ID &&
        withSearchResults &&
        isLoading === false &&
        searchDone
      ) {
        trackSearchResults();
      }
    },
    [productIds, isLoading],
  );

  useEffect(
    () => {
      if (
        !isLoading &&
        noResults &&
        props.i18n.remote?.noResult?.recommendation?.resortIds?.length > 0
      ) {
        dispatch(
          facetingLoadProductsRequest({
            appContext,
            productIds: props.i18n.remote.noResult.recommendation.resortIds,
          }),
        );
      }
    },
    [products],
  );

  const cleanSearch = async () => dispatch(resetSearch());
  const cleanCriteria = async () => dispatch(resetCriteria());

  useEffect(() => {
    dispatch(setProductComparisonSelectionMode(false));

    const { initialIsSearchEnabled = false } = props;

    if (!currentUrlContainsSearchQueryParam(location)) {
      // We always want to reset the search query, unless it's in the URL.
      dispatch(changeSearchQuery({ label: '' }));
    }

    // useEffect cannot be async because it's supposed to return a cleaning callback.
    // This immediatly invoked function is a workaround to this.
    (async function() {
      if (currentUrlHasNoQueryParams(location) && props.resetSearchAtMount) {
        await cleanSearch();
        await cleanCriteria();
      }

      if (departureCity) {
        // If transport is set in store, we need to trigger
        // the search request to get the departureCities facet from Pertimm.
        dispatch(setIsSearchEnabled(true));
      } else {
        dispatch(setIsSearchEnabled(initialIsSearchEnabled));
      }
    })();

    return () => {
      cleanSearch();
    };
  }, []);

  useEffect(
    () => {
      // In case PERTIMM response indicates a redirection, go to this page...
      if (redirection) {
        dispatch(startLoading());
        redirectUtils.redirectTo()(redirection);

        return () => {
          dispatch(resetRedirection());
        };
      }
    },
    [redirection],
  );

  const filtersKeysToIgnore = [
    DEPARTURE_DATE_MAX_FACET_ID,
    DEPARTURE_DATE_MIN_FACET_ID,
    DURATION_FACET,
    PRODUCT_FACET_ID,
  ];

  useEffect(
    () => {
      search();
    },
    [
      JSON.stringify(kids),
      JSON.stringify(query),
      isSearchEnabled,
      departureDate,
      duration,
      JSON.stringify(
        (facetingCriteria.filters || []).filter(facet => {
          return !filtersKeysToIgnore.includes(facet.id);
        }),
      ),
      props.offer,
    ],
  );

  const onSubmit = (event?: Event) => {
    const { searchPageRouteName } = props;

    if (event) {
      event.preventDefault();
    }
    const searchQuery = keywords ? { label: keywords } : query;

    if (keywords) {
      dispatch(changeSearchQuery(searchQuery));
    }

    if (!keywords && shouldRedirectToResortPage && searchQuery.type === RESORT_PRODUCT_TYPE) {
      trackQueryChange(searchQuery, { internalSearchEngineNbResults: 'redirection' });
      trackSearchResults({ internalSearchEngineNbResults: 'redirection', searchResorts: [] });

      navigateToResortPage({
        location,
        query: searchQuery,
        searchCriteria,
      });
    } else {
      if (!withSearchResults) {
        trackQueryChange(searchQuery);
        cleanSearch();
      }
      navigateToSearchPage({
        criteriaKeysToPersist: FACET_QUERY_PARAMS,
        location,
        searchCriteria,
        searchPageRouteName,
      });
    }
  };

  return (
    <div data-testid="facetSearch" className="relative z-3">
      {props.withSearchForm && (
        <CriteriaContextProvider
          value={{
            trackingActionName: 'smartCriterias',
          }}
        >
          <CriteriaDisplayProvider>
            <SearchForm
              doTrackQueryChange={withSearchResults}
              i18n={props.i18n}
              onSubmit={onSubmit}
              withFacets={props.withSearchFacets}
              withQuery={!props.hideSearchInput}
              showNbResultsInLayer={props.withSearchResults}
              layerButtonLabel={
                props.withSearchResults
                  ? (props.i18n.remote && props.i18n.remote.searchAction) || 'search'
                  : props.i18n.booking.editCriteria.validateCriteria
              }
            />
          </CriteriaDisplayProvider>
        </CriteriaContextProvider>
      )}
      {props.withSearchResults && (
        <FacetSearchResults
          i18n={props.i18n}
          initialProducts={props.initialProducts}
          isProductComparisonModeAuthorized={props.isProductComparisonModeAuthorized}
          offer={props.offer}
          resultsHasSeeMore={props.resultsHasSeeMore}
          resultsSeeMore={props.resultsSeeMore}
          isSynchronousGrid={isSynchronousGrid}
          searchOptions={searchOptions}
          shouldSetDefaultDepartureCityOnCompare={props.shouldSetDefaultDepartureCityOnCompare}
          virtualizedGrid={props.virtualizedGrid}
        />
      )}
    </div>
  );
};
